// Shared clustering utilities for annotations

export const CLUSTER_PX_THRESHOLD = 16; // pixels
export const CLUSTER_PERCENT_THRESHOLD = 0.25; // percentage points
export const SEVERITY_Y = { critical: 20, high: 35, medium: 50, low: 65, default: 50 };

// Map severity to Y percent
export function getYPercentForSeverity(severity) {
  return (SEVERITY_Y[severity] !== undefined && SEVERITY_Y[severity] !== null)
    ? SEVERITY_Y[severity]
    : SEVERITY_Y.default;
}

// Group items by horizontal proximity. Items should be of the form:
// { xPercent: number, xPx: number, yPercent: number, ref: any, color: string }
export function groupByProximity(prepared, containerWidth, options = {}) {
  const pxThreshold = (options && options.pxThreshold) !== undefined
    ? options.pxThreshold
    : CLUSTER_PX_THRESHOLD;
  const pctThreshold = (options && options.percentThreshold) !== undefined
    ? options.percentThreshold
    : CLUSTER_PERCENT_THRESHOLD;
  const groups = [];
  const list = [...prepared].sort((a, b) => a.xPx - b.xPx);
  let current = [];

  const close = (a, b) => {
    const pxClose = containerWidth > 0 && Math.abs(a.xPx - b.xPx) <= pxThreshold;
    const pctClose = Math.abs(a.xPercent - b.xPercent) <= pctThreshold;
    return pxClose || pctClose;
  };

  const flush = () => {
    if (current.length === 0) return;
    if (current.length === 1) {
      const c0 = current[0];
      groups.push({ key: c0.ref.id || `${c0.xPercent}-1`, items: [...current], xPercent: c0.xPercent, yPercent: c0.yPercent, color: c0.color });
    } else {
      const xPercentAvg = current.reduce((s, i) => s + i.xPercent, 0) / current.length;
      const ys = current.map(i => i.yPercent).sort((a, b) => a - b);
      const yPercentMed = ys[Math.floor(ys.length / 2)];
      groups.push({ key: `x${xPercentAvg.toFixed(2)}-${current.length}`, items: [...current], xPercent: xPercentAvg, yPercent: yPercentMed, color: current[0].color });
    }
    current = [];
  };

  list.forEach((item) => {
    if (current.length === 0) { current.push(item); return; }
    const last = current[current.length - 1];
    if (close(item, last)) current.push(item);
    else { flush(); current.push(item); }
  });
  flush();

  return groups;
}

// Merge a set of time-range items into clusters of transitively-overlapping
// ranges. Non-overlapping ranges stay separate. A small `gap` epsilon fuses
// sub-unit slivers (e.g. sub-pixel) without bridging real gaps.
//
// items: [{ ref, start, end, type }] with start/end comparable numbers
//        (pixels or percent — same unit as `gap`).
// returns: [{ start, end, items: [ref...], homogeneousType: string|null }]
//          where start/end are the union span and items preserve input order
//          within the cluster (sorted by start).
export function mergeOverlappingRanges(items, { gap = 0 } = {}) {
  const sorted = [...items]
    .filter(it => it && Number.isFinite(it.start) && Number.isFinite(it.end))
    .map(it => (it.end < it.start ? { ...it, start: it.end, end: it.start } : it))
    .sort((a, b) => (a.start - b.start) || (a.end - b.end));

  const clusters = [];
  let current = null;

  const finalize = () => {
    if (!current) return;
    const firstType = current.items[0] && current.items[0].type;
    const homogeneousType = current.items.every(it => it.type === firstType)
      ? (firstType || null)
      : null;
    clusters.push({
      start: current.start,
      end: current.end,
      items: current.items.map(it => it.ref),
      homogeneousType
    });
    current = null;
  };

  sorted.forEach((it) => {
    if (!current) {
      current = { start: it.start, end: it.end, items: [it] };
      return;
    }
    if (it.start <= current.end + gap) {
      current.end = Math.max(current.end, it.end);
      current.items.push(it);
    } else {
      finalize();
      current = { start: it.start, end: it.end, items: [it] };
    }
  });
  finalize();

  return clusters;
}
